import React, { useState, useEffect, useMemo, useRef } from 'react';
import OnOffField from '../OnOffField';
import OfficeSelect from './OfficeSelect';
import OfficeGroupSelect from './OfficeGroupSelect';
import OfficeMultiChoice from './MultiChoice/OfficeMultiChoice';
import OfficeGroupMultiSelect from './MultiChoice/OfficeGroupMultiSelect';
import ErrorMessage from '../../../ErrorMessage';
import OfficeService from '../../../../Services/OfficeService';
import OfficeHelper from '../../../../Helpers/ModelHelpers/OfficeHelper';

/**
 * Office search filter that selects office groups or, with its switch moved from "Groups" to "Offices", single offices
 * (CD-5311). The switch sits beside the select, like the other switched inputs (inputCheckBoxCombo); its column fits
 * the two labels instead of On/Off. Its single selects never show the "Type to search..." box, whatever the field
 * definition says: the input sets their showSelectFilterAt itself.
 * isMulti and forReport come from the input type (renderOfficeOrGroupSelect), so it shows one of four selects: one
 * group, one office, several groups or several offices. Its value is always a list of office ids; in group mode, every
 * office of the chosen groups. It opens in group mode when the value is empty or made of whole groups
 * (OfficeHelper.OpensInGroupMode; one group at most for a single select), so a saved search reopens the way it was
 * made. A single id saved before a field became a list, and the eraser's empty value, are turned into a list. Ids keep
 * the type of the value it was given (string or number). forReport limits it to the offices the user may report on;
 * with selectAllOnLoad (a report field's placeholder 'select_all') an empty value starts with all of them selected.
 */
const OfficeOrGroupSelect = ({
  value = null,
  className = 'w-100 form-control form-control-sm',
  filterCleared = false,
  fieldName = 'defaultFieldname',
  onChange = (_fieldName, _officeIds) => {},
  isMulti = false,
  forReport = false,
  selectAllOnLoad = false,
}) => {
  const officeIds = OfficeHelper.ToOfficeIdList(value);
  const asString = officeIds.some((officeId) => typeof officeId === 'string');
  const [offices, setOffices] = useState(() => (forReport ? null : OfficeHelper.GetOffices()));
  const [isOfficeMode, setIsOfficeMode] = useState(() => !OfficeHelper.OpensInGroupMode(officeIds, !isMulti));
  const emittedOfficeIds = useRef(officeIds);

  const emit = (newOfficeIds) => {
    const typedOfficeIds = newOfficeIds.map((officeId) => (asString ? `${officeId}` : Number(officeId)));
    emittedOfficeIds.current = typedOfficeIds;
    onChange(fieldName, typedOfficeIds);
  };

  useEffect(() => {
    if (!forReport) {
      return;
    }
    OfficeService.GetOfficesByReportRole()
      .then((reportOffices) => {
        const reportOfficeIds = (reportOffices ?? []).map((office) => `${office.officeId}`);
        const officesForReport = OfficeHelper.GetOffices().filter((office) =>
          reportOfficeIds.includes(`${office.officeId}`)
        );
        setOffices(officesForReport);
        if (selectAllOnLoad && officeIds.length === 0) {
          emit(officesForReport.map((office) => office.officeId));
        }
      })
      .catch(() => {
        ErrorMessage.SetErrorMessage('OOGS49');
      });
  }, [forReport]);

  // A value that did not come from this input (a saved search, the eraser) decides the mode again, and one that is not
  // a list yet is turned into one.
  useEffect(() => {
    if (`${officeIds}` !== `${emittedOfficeIds.current}`) {
      setIsOfficeMode(!OfficeHelper.OpensInGroupMode(officeIds, !isMulti, offices ?? undefined));
    }
    if (!Array.isArray(value) && value !== null && value !== undefined) {
      emit(officeIds);
      return;
    }
    emittedOfficeIds.current = officeIds;
  }, [value]);

  const reportOfficeOptions = useMemo(
    () => (forReport && offices ? offices.map((office) => ({ label: office.name, value: office.officeId })) : null),
    [forReport, offices]
  );

  // Switched on, a single select keeps one office of its group; switched off, the selection widens to whole groups.
  const changeMode = (officeMode) => {
    setIsOfficeMode(officeMode);
    if (officeMode) {
      if (!isMulti) {
        emit(officeIds.slice(0, 1));
      }
      return;
    }
    const officeGroupIds = OfficeHelper.GetOfficeGroupIdsOfOffices(officeIds, offices ?? undefined);
    const keptOfficeGroupIds = isMulti ? officeGroupIds : officeGroupIds.slice(0, 1);
    emit(OfficeHelper.GetOfficeIdsOfOfficeGroups(keptOfficeGroupIds, asString, offices ?? undefined));
  };

  const renderSelect = () => {
    if (isMulti && isOfficeMode) {
      return (
        <OfficeMultiChoice
          selected={officeIds}
          onChange={emit}
          customOfficeList={reportOfficeOptions}
        />
      );
    }
    if (isMulti) {
      return (
        <OfficeGroupMultiSelect
          selected={OfficeHelper.GetOfficeGroupIdsOfOffices(officeIds, offices)}
          offices={offices}
          onChange={(officeGroupIds) =>
            emit(OfficeHelper.GetOfficeIdsOfOfficeGroups(officeGroupIds, asString, offices))
          }
        />
      );
    }
    if (isOfficeMode) {
      return (
        <OfficeSelect
          value={officeIds[0] ?? ''}
          className={className}
          fieldName={fieldName}
          filterCleared={filterCleared}
          onChange={(_, officeId) => emit(officeId === '' ? [] : [officeId])}
          showSelectFilterAt={Number.MAX_SAFE_INTEGER}
        />
      );
    }
    return (
      <OfficeGroupSelect
        value={OfficeHelper.GetOfficeGroupIdsOfOffices(officeIds, offices)[0] ?? ''}
        className={className}
        fieldName={fieldName}
        filterCleared={filterCleared}
        onChange={(_, officeGroupId) =>
          emit(officeGroupId === '' ? [] : OfficeHelper.GetOfficeIdsOfOfficeGroups([officeGroupId], asString, offices))
        }
        showSelectFilterAt={Number.MAX_SAFE_INTEGER}
      />
    );
  };

  if (offices === null) {
    return null;
  }

  return (
    <div className='inputCheckBoxCombo'>
      <div className='figmaOnOffButtonBox figmaOnOffButtonBoxFitLabel'>
        <OnOffField
          value={isOfficeMode}
          onChange={changeMode}
          onLabel='Offices'
          offLabel='Groups'
        />
      </div>
      <div>{renderSelect()}</div>
    </div>
  );
};

export default OfficeOrGroupSelect;
